import {
	PropsWithChildren,
	ReactElement,
	Ref,
	RefObject,
	createContext,
	forwardRef,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	KeyboardEvent,
	startTransition,
	useImperativeHandle
} from "react";
import {
	MultiSelectableOptions,
	useResizeObserver,
	useStableImperativeCallback,
	useSynchronizedRef
} from "client/Hooks";
import {
	GridItem,
	LassoContainer,
	LassoContainerContext,
	LassoMode,
	doOverlap,
	lassoModeToScopeKind
} from "client/Components";
import styles from "./GridView.module.less";
import {
	groupGridViewItems,
	GridViewGroup,
	GridViewGroupItem,
	GridViewItemUniqueKey
} from "./groupGridViewItems";
import { Rect, Key } from "client/Misc/commonTypes";
import { SizeInformation } from "client_legacy/ndaw/sizing";
import { isNil, setTrackingId } from "client/Misc/utils";
import {
	FocusItemAtTarget,
	GridNavigationDirection,
	KeyboardGridNavigationContext,
	useKeyboardGridNavigation,
	useKeyboardGridNavigationItem
} from "client/Hooks/useKeyboardGridNavigation";
import { never } from "client_legacy/ndaw/errors";
import { outOfContextCall } from "client/Misc/reactUtils";

export type GridViewProps<TModel> = {
	items: readonly GridViewGroupItem<TModel>[];
	trackingId: string;
	multiSelection?: MultiSelectableOptions;
	hasLassoSelection?: boolean;
	children(item: GridViewGroupItem<TModel>): ReactElement;
	elementToRenderAfterItems?: ReactElement;
};

export const GridViewInternal = forwardRef(
	<TModel, >(props: GridViewProps<TModel>, ref: Ref<HTMLDivElement>): ReactElement => {
		const {
			items,
			trackingId,
			multiSelection,
			hasLassoSelection = false,
			elementToRenderAfterItems,
			children
		} = props;

		const groupNavigationRefs: RefObject<GroupContainerHandle[]> = useRef<GroupContainerHandle[]>([]);

		const groups = useMemo(
			(): readonly GridViewGroup<TModel>[] => {
				return groupGridViewItems(items);
			},
			[items]
		);

		const [tabbableItem, setTabbableItem] = useState<TabbableItem | null>(null);

		const computedTabbableItem = useMemo(
			(): TabbableItem => {
				if (isNil(tabbableItem)) {
					return {
						index: 0,
						uniqueKey: groups[0]?.uniqueKey ?? null
					};
				}

				return tabbableItem;
			},
			[tabbableItem, groups]
		);

		const changeTabbableItemHandler = useCallback(
			(item: TabbableItem): void => {
				startTransition(
					(): void => {
						setTabbableItem(item);
					}
				);
			},
			[setTabbableItem]
		);

		const lassoStartHandler = useCallback(
			(lassoMode: LassoMode): void => {
				multiSelection?.startSelection(lassoModeToScopeKind[lassoMode]);
			},
			[multiSelection]
		);

		const lassoStopHandler = useCallback(
			(): void => {
				multiSelection?.finishSelection();
			},
			[multiSelection]
		);

		const isLassoDisabled: boolean = !multiSelection
			|| !hasLassoSelection
			|| groups.length === 0;

		const setNavigationRef = useCallback(
			(
				index: number,
				ref: GroupContainerHandle | null
			): void => {
				if (ref === null) {
					groupNavigationRefs
						.current
						.splice(index, 1);

					return;
				}

				groupNavigationRefs.current[index] = ref;
			},
			[]
		);

		const outOfBoundsHandler = useCallback(
			(
				index: number,
				direction: GridNavigationDirection,
				rect: Rect
			): void => {
				const {
					current: groupsNavigation
				} = groupNavigationRefs;

				const totalGroups: number = groupsNavigation.length;

				if (totalGroups === 0) {
					console.error("There are no group navigation handlers");
				}

				switch (direction) {
					case Key.ArrowDown:
						if (index < totalGroups - 1) {
							const nextHandler: GroupContainerHandle | undefined = groupsNavigation[index + 1];
							nextHandler?.focusItemAtHandler({
								direction: Key.ArrowDown,
								rect
							});
						}
					break;
					case Key.ArrowUp:
						if (index > 0) {
							const prevHandler: GroupContainerHandle | undefined = groupsNavigation[index - 1];
							prevHandler?.focusItemAtHandler({
								direction: Key.ArrowUp,
								rect
							});
						}
						break;
					case Key.ArrowRight:
						if (index < totalGroups - 1) {
							const nextHandler: GroupContainerHandle | undefined = groupsNavigation[index + 1];
							nextHandler?.focusItemAtHandler("start");
						}
						break;
					case Key.ArrowLeft:
						if (index > 0) {
							const prevHandler: GroupContainerHandle | undefined = groupsNavigation[index - 1];
							prevHandler?.focusItemAtHandler("end");
						}
						break;
					default:
						never(direction);
				}
			},
			[]
		);

		return (
			<div
				{...setTrackingId(trackingId)}
				className={styles.root}
				ref={ref}
			>
				<LassoContainer
					onLassoStart={lassoStartHandler}
					onLassoStop={lassoStopHandler}
					isDisabled={isLassoDisabled}
				>
					<div className={styles.container}>
						{groups.map(
							(group: GridViewGroup<TModel>, index: number): ReactElement => (
								<GroupContainer
									key={group.uniqueKey}
									group={group}
									renderItem={children}
									ref={(ref) => setNavigationRef(index, ref)}
									onTabbableIndexChange={(index: number): void => {
										changeTabbableItemHandler({
											index,
											uniqueKey: group.uniqueKey
										});
									}}
									onTabbableLost={() => setTabbableItem(null)}
									tabbableIndex={computedTabbableItem.uniqueKey === group.uniqueKey
										? computedTabbableItem.index
										: undefined
									}
									onOutOfBounds={(direction, rect) => outOfBoundsHandler(index, direction, rect)}
								/>
							)
						)}
						{elementToRenderAfterItems}
					</div>
				</LassoContainer>
			</div>
		);
	}
);

GridViewInternal.displayName = "GridView";

type TabbableItem = {
	index: number;
	uniqueKey: GridViewItemUniqueKey | null;
};

type GridViewWithSubComponents = {
	<TItem, >(props: GridViewProps<TItem> & { ref?: Ref<HTMLDivElement>; }): ReactElement;
	displayName: string;
	Item: typeof GridItem;
};

export const GridView = GridViewInternal as unknown as GridViewWithSubComponents;
GridView.Item = GridItem;

type ItemContainerProps = {
	uniqueKey: GridViewItemUniqueKey;
	index: number;
	tabbableIndex?: number;
	onClick: () => void;
	onTabbableLost: () => void;
};

const ItemContainer = (props: PropsWithChildren<ItemContainerProps>): ReactElement => {
	const {
		uniqueKey,
		index,
		children,
		tabbableIndex,
		onClick,
		onTabbableLost
	} = props;

	const [doesOverlapWithLasso, setDoesOverlapWithLasso] = useState<boolean>(false);
	const [itemRect, setItemRect] = useState<Rect>({
		left: 0,
		top: 0,
		right: 0,
		bottom: 0
	});

	const itemRef: RefObject<HTMLLIElement | null> = useRef<HTMLLIElement>(null);
	const itemSize: SizeInformation = useResizeObserver(itemRef);

	const {
		hasLassoContainer,
		lassoRect,
		containerRect
	} = useContext(LassoContainerContext);

	const hasLassoRect: boolean = hasLassoContainer && Boolean(lassoRect);

	const {
		ref,
		onKeyDown
	} = useKeyboardGridNavigationItem({
		index
	});

	const isTabbable: boolean = tabbableIndex === index;

	const contextData = useMemo(
		(): ContextData => ({
			doesOverlapWithLasso,
			hasLassoRect,
			ref,
			onKeyDown,
			tabIndex: isTabbable ? 0 : -1
		}),
		[
			doesOverlapWithLasso,
			hasLassoRect,
			ref,
			onKeyDown,
			isTabbable
		]
	);

	const isTabbableRef: Readonly<RefObject<boolean>> = useSynchronizedRef(isTabbable);
	const stableOnTabbableLost: () => void = useStableImperativeCallback(onTabbableLost);

	useEffect(
		(): (() => void) => {
			const isTabbable = isTabbableRef.current;

			return (): void => {
				if (isTabbable) {
					stableOnTabbableLost();
				}
			};
		},
		[isTabbableRef, stableOnTabbableLost]
	);

	useLayoutEffect(
		() => {
			if (!hasLassoContainer || !itemRef.current) {
				return;
			}

			const {
				left,
				top,
				right,
				bottom
			} = itemRef
				.current
				.getBoundingClientRect();

			const xOffset: number = containerRect.left;
			const yOffset: number = containerRect.top;

			setItemRect({
				left: left - xOffset,
				top: top - yOffset,
				right: right - xOffset,
				bottom: bottom - yOffset
			});
		},
		[
			containerRect.left,
			containerRect.top,
			hasLassoContainer,
			itemSize.width
		]
	);

	useEffect(
		() => {
			if (!hasLassoContainer || !lassoRect || !itemRect) {
				setDoesOverlapWithLasso(false);
			} else {
				setDoesOverlapWithLasso(
					doOverlap(lassoRect, itemRect)
				);
			}
		},
		[
			hasLassoContainer,
			lassoRect,
			itemRect
		]
	);

	return (
		<li
			{...setTrackingId("grid-view-item", `|${uniqueKey}`)}
			ref={itemRef}
			onClick={onClick}
		>
			<GridViewItemContainerContext.Provider value={contextData}>
				{children}
			</GridViewItemContainerContext.Provider>
		</li>
	);
};

ItemContainer.displayName = "ItemContainer";

type GroupContainerHandle = {
	focusItemAtHandler: (target: FocusItemAtTarget) => void;
};

type GroupContainerProps<TModel> = {
	group: GridViewGroup<TModel>;
	renderItem: (item: GridViewGroupItem<TModel>) => ReactElement;
	tabbableIndex?: number;
	onTabbableIndexChange: (index: number) => void;
	ref: Ref<GroupContainerHandle>;
	onOutOfBounds: (direction: GridNavigationDirection, rect: Rect) => void;
	onTabbableLost: () => void;
};

const GroupContainer = <TModel, >(props: GroupContainerProps<TModel>): ReactElement => {
	const {
		group,
		renderItem,
		tabbableIndex,
		onTabbableIndexChange,
		ref,
		onOutOfBounds,
		onTabbableLost
	} = props;

	const {
		contextData,
		focusItemAtHandler
	} = useKeyboardGridNavigation({
		onFocusChange: onTabbableIndexChange,
		onOutOfBounds
	});

	useImperativeHandle(
		ref,
		(): GroupContainerHandle => ({
			focusItemAtHandler
		}),
		[focusItemAtHandler]
	);

	return (
		<ul
			className={styles.list}
		>
			<KeyboardGridNavigationContext.Provider value={contextData}>
				{group.items
					.map(
						(x: GridViewGroupItem<TModel>, index: number): ReactElement => (
							<ItemContainer
								key={x.uniqueKey}
								uniqueKey={x.uniqueKey}
								index={index}
								tabbableIndex={tabbableIndex}
								onClick={() => onTabbableIndexChange(index)}
								onTabbableLost={onTabbableLost}
							>
								{renderItem(x)}
							</ItemContainer>
						)
					)
				}
			</KeyboardGridNavigationContext.Provider>
		</ul>
	);
};

GroupContainer.displayName = "GroupContainer";

type ContextData = {
	doesOverlapWithLasso: boolean;
	hasLassoRect: boolean;
	ref: (element: HTMLElement | null) => void;
	onKeyDown: (args: KeyboardEvent<HTMLElement>) => boolean;
	tabIndex: number;
};

export const GridViewItemContainerContext = createContext<ContextData>({
	doesOverlapWithLasso: false,
	hasLassoRect: false,
	ref: outOfContextCall("GridViewItemContainerContext", "ref"),
	onKeyDown: (): boolean => {
		outOfContextCall("GridViewItemContainerContext", "onKeyDown");
		return false;
	},
	tabIndex: 0
});